'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { Dialog as DialogPrimitive } from 'radix-ui';
import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { ArrowLeft01Icon, ArrowRight01Icon, Cancel01Icon, Copy01Icon, Download01Icon, Loading03Icon, ViewOffIcon } from '@/components/ui/icon/glyphs';
import { runWithCleanup } from '@/lib/async/run-with-cleanup';
import { reportError } from '@/lib/telemetry';
import { showToast } from '@/lib/ui/toast';
import { cn } from '@/lib/ui/utils';
import { useLightboxStore } from '@/stores/lightbox';
import { useNsfwRevealStore } from '@/stores/nsfw-reveal';
const SWIPE_THRESHOLD = 50;
export function LightboxModal() {
  const $ = _c(111);
  const t = useTranslations("lightbox");
  const tNsfw = useTranslations("image.nsfw");
  const open = useLightboxStore(_temp);
  const images = useLightboxStore(_temp2);
  const currentIndex = useLightboxStore(_temp3);
  const closeLightbox = useLightboxStore(_temp4);
  const goToNext = useLightboxStore(_temp5);
  const goToPrev = useLightboxStore(_temp6);
  const currentImageForReveal = images[currentIndex];
  let t0;
  if ($[0] !== currentImageForReveal) {
    t0 = s_5 => currentImageForReveal !== undefined ? s_5.isRevealed(currentImageForReveal.src) : false;
    $[0] = currentImageForReveal;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isCurrentRevealed = useNsfwRevealStore(t0);
  const showHideAgain = currentImageForReveal !== undefined && currentImageForReveal.wasNsfw === true && isCurrentRevealed;
  let t1;
  if ($[2] !== closeLightbox || $[3] !== currentImageForReveal) {
    t1 = function handleHideAgain() {
      if (currentImageForReveal === undefined) {
        return;
      }
      useNsfwRevealStore.getState().unreveal(currentImageForReveal.src);
      closeLightbox();
    };
    $[2] = closeLightbox;
    $[3] = currentImageForReveal;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const handleHideAgain = t1;
  const touchStartX = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setImageLoaded(false);
    };
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== currentIndex || $[7] !== open) {
    t3 = [open, currentIndex];
    $[6] = currentIndex;
    $[7] = open;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  useEffect(t2, t3);
  const currentImage = images[currentIndex];
  const hasMultiple = images.length > 1;
  let t4;
  let t5;
  if ($[9] !== goToNext || $[10] !== goToPrev || $[11] !== open) {
    t4 = () => {
      if (!open) {
        return;
      }
      const handleKeyDown = function handleKeyDown(e) {
        if (e.key === "ArrowLeft") {
          goToPrev();
        } else {
          if (e.key === "ArrowRight") {
            goToNext();
          }
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => {
        window.removeEventListener("keydown", handleKeyDown);
      };
    };
    t5 = [open, goToNext, goToPrev];
    $[9] = goToNext;
    $[10] = goToPrev;
    $[11] = open;
    $[12] = t4;
    $[13] = t5;
  } else {
    t4 = $[12];
    t5 = $[13];
  }
  useEffect(t4, t5);
  let t6;
  if ($[14] !== closeLightbox) {
    t6 = function handleOpenChange(nextOpen) {
      if (!nextOpen) {
        closeLightbox();
      }
    };
    $[14] = closeLightbox;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const handleOpenChange = t6;
  let t7;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = function handleTouchStart(e_0) {
      const touch = e_0.touches[0];
      if (touch !== undefined) {
        touchStartX.current = touch.clientX;
      }
    };
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const handleTouchStart = t7;
  let t8;
  if ($[17] !== goToNext || $[18] !== goToPrev) {
    t8 = function handleTouchEnd(e_1) {
      if (touchStartX.current === null) {
        return;
      }
      const touch_0 = e_1.changedTouches[0];
      if (touch_0 === undefined) {
        return;
      }
      const diff = touch_0.clientX - touchStartX.current;
      touchStartX.current = null;
      if (Math.abs(diff) >= SWIPE_THRESHOLD) {
        if (diff < 0) {
          goToNext();
        } else {
          goToPrev();
        }
      }
    };
    $[17] = goToNext;
    $[18] = goToPrev;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const handleTouchEnd = t8;
  let t9;
  if ($[20] !== currentImage || $[21] !== isDownloading) {
    t9 = async function handleDownload() {
      if (currentImage === undefined || isDownloading) {
        return;
      }
      setIsDownloading(true);
      ;
      try {
        await runWithCleanup(async () => {
          const response = await fetch(currentImage.src);
          if (!response.ok) {
            throw new Error(`Lightbox download failed with HTTP ${String(response.status)}`);
          }
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          const parts = currentImage.src.split("/");
          const filename = parts[parts.length - 1] ?? "image";
          const a = document.createElement("a");
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }, () => setIsDownloading(false));
      } catch (t10) {
        const err = t10;
        reportError(err, {
          feature: "lightbox-download"
        });
      }
    };
    $[20] = currentImage;
    $[21] = isDownloading;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const handleDownload = t9;
  let t10;
  if ($[23] !== currentImage || $[24] !== t) {
    t10 = function handleCopyUrl() {
      if (currentImage === undefined) {
        return;
      }
      navigator.clipboard.writeText(currentImage.src);
      showToast({
        id: "image-url-copied",
        message: t("urlCopied"),
        variant: "success"
      });
    };
    $[23] = currentImage;
    $[24] = t;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const handleCopyUrl = t10;
  let t11;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <DialogPrimitive.Overlay className={cn("fixed inset-0 z-modal bg-surface-scrim-strong", "data-[state=closed]:animate-out data-[state=closed]:fade-out-0", "data-[state=open]:animate-in data-[state=open]:fade-in-0")} />;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = cn("fixed inset-0 z-modal flex flex-col items-center justify-center outline-none", "data-[state=closed]:animate-out data-[state=closed]:fade-out-0", "data-[state=open]:animate-in data-[state=open]:fade-in-0");
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== closeLightbox) {
    t13 = e_2 => {
      if (e_2.target === e_2.currentTarget) {
        closeLightbox();
      }
    };
    $[28] = closeLightbox;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t) {
    t14 = t("previewTitle");
    $[30] = t;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== currentImage?.alt || $[33] !== t) {
    t15 = currentImage?.alt ?? t("previewTitle");
    $[32] = currentImage?.alt;
    $[33] = t;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t15) {
    t16 = <DialogPrimitive.Title className="sr-only">{t15}</DialogPrimitive.Title>;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t) {
    t17 = t("previewDescription");
    $[37] = t;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  const t18 = hasMultiple ? ` Image ${String(currentIndex + 1)} of ${String(images.length)}.` : "";
  let t19;
  if ($[39] !== t17 || $[40] !== t18) {
    t19 = <DialogPrimitive.Description className="sr-only">{t17}{t18}</DialogPrimitive.Description>;
    $[39] = t17;
    $[40] = t18;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== t) {
    t20 = t("close");
    $[42] = t;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Icon icon={Cancel01Icon} className="size-5" />;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  let t22;
  if ($[45] !== closeLightbox || $[46] !== t20) {
    t22 = <button type="button" onClick={closeLightbox} className="absolute top-4 right-4 z-10 rounded-full bg-surface-scrim-default p-2 text-white transition-colors hover:bg-surface-scrim-strong" aria-label={t20}>{t21}</button>;
    $[45] = closeLightbox;
    $[46] = t20;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== goToNext || $[49] !== goToPrev || $[50] !== hasMultiple || $[51] !== t) {
    t23 = hasMultiple && <><button type="button" onClick={goToPrev} className="absolute left-4 z-10 rounded-full bg-surface-scrim-default p-2 text-white transition-colors hover:bg-surface-scrim-strong" aria-label={t("previous")}><Icon icon={ArrowLeft01Icon} className="size-6" /></button><button type="button" onClick={goToNext} className="absolute right-4 z-10 rounded-full bg-surface-scrim-default p-2 text-white transition-colors hover:bg-surface-scrim-strong" aria-label={t("next")}><Icon icon={ArrowRight01Icon} className="size-6" /></button></>;
    $[48] = goToNext;
    $[49] = goToPrev;
    $[50] = hasMultiple;
    $[51] = t;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  let t24;
  if ($[53] !== currentImage || $[54] !== imageLoaded) {
    t24 = currentImage !== undefined && <>{!imageLoaded && <Icon icon={Loading03Icon} className="absolute size-8 animate-spin text-white/60" />}<img src={currentImage.src} alt={currentImage.alt} loading="eager" onLoad={() => setImageLoaded(true)} className={cn("max-h-[calc(100dvh-10rem)] max-w-[calc(100vw-4rem)] object-contain transition-opacity duration-normal", imageLoaded ? "opacity-100" : "opacity-0")} draggable={false} /></>;
    $[53] = currentImage;
    $[54] = imageLoaded;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] !== t24) {
    t25 = <div className="flex max-h-[calc(100dvh-8rem)] max-w-[calc(100vw-2rem)] items-center justify-center p-4">{t24}</div>;
    $[56] = t24;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] !== handleDownload) {
    t26 = () => {
      handleDownload();
    };
    $[58] = handleDownload;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  let t27;
  if ($[60] !== t) {
    t27 = t("download");
    $[60] = t;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  let t28;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <Icon icon={Download01Icon} className="size-4" />;
    $[62] = t28;
  } else {
    t28 = $[62];
  }
  let t29;
  if ($[63] !== t) {
    t29 = t("download");
    $[63] = t;
    $[64] = t29;
  } else {
    t29 = $[64];
  }
  let t30;
  if ($[65] !== t29) {
    t30 = <span className="hidden sm:inline">{t29}</span>;
    $[65] = t29;
    $[66] = t30;
  } else {
    t30 = $[66];
  }
  let t31;
  if ($[67] !== isDownloading || $[68] !== t26 || $[69] !== t27 || $[70] !== t30) {
    t31 = <button type="button" onClick={t26} disabled={isDownloading} className="flex items-center gap-1.5 transition-opacity hover:opacity-80 disabled:opacity-50" aria-label={t27}>{t28}{t30}</button>;
    $[67] = isDownloading;
    $[68] = t26;
    $[69] = t27;
    $[70] = t30;
    $[71] = t31;
  } else {
    t31 = $[71];
  }
  let t32;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <div className="h-4 w-px bg-background-primary/30" />;
    $[72] = t32;
  } else {
    t32 = $[72];
  }
  let t33;
  if ($[73] !== t) {
    t33 = t("copyUrl");
    $[73] = t;
    $[74] = t33;
  } else {
    t33 = $[74];
  }
  let t34;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <Icon icon={Copy01Icon} className="size-4" />;
    $[75] = t34;
  } else {
    t34 = $[75];
  }
  let t35;
  if ($[76] !== t) {
    t35 = t("copyUrl");
    $[76] = t;
    $[77] = t35;
  } else {
    t35 = $[77];
  }
  let t36;
  if ($[78] !== t35) {
    t36 = <span className="hidden sm:inline">{t35}</span>;
    $[78] = t35;
    $[79] = t36;
  } else {
    t36 = $[79];
  }
  let t37;
  if ($[80] !== handleCopyUrl || $[81] !== t33 || $[82] !== t36) {
    t37 = <button type="button" onClick={handleCopyUrl} className="flex items-center gap-1.5 transition-opacity hover:opacity-80" aria-label={t33}>{t34}{t36}</button>;
    $[80] = handleCopyUrl;
    $[81] = t33;
    $[82] = t36;
    $[83] = t37;
  } else {
    t37 = $[83];
  }
  let t38;
  if ($[84] !== currentIndex || $[85] !== hasMultiple || $[86] !== images.length) {
    t38 = hasMultiple && <><div className="h-4 w-px bg-background-primary/30" /><span>{currentIndex + 1} / {images.length}</span></>;
    $[84] = currentIndex;
    $[85] = hasMultiple;
    $[86] = images.length;
    $[87] = t38;
  } else {
    t38 = $[87];
  }
  let t39;
  if ($[88] !== handleHideAgain || $[89] !== showHideAgain || $[90] !== tNsfw) {
    t39 = showHideAgain && <><div className="h-4 w-px bg-background-primary/30" /><AnimatedButton variant="secondary" size="xs" prefix={ViewOffIcon} onClick={handleHideAgain} aria-label={tNsfw("hide_again")}>{tNsfw("hide_again")}</AnimatedButton></>;
    $[88] = handleHideAgain;
    $[89] = showHideAgain;
    $[90] = tNsfw;
    $[91] = t39;
  } else {
    t39 = $[91];
  }
  let t40;
  if ($[92] !== t31 || $[93] !== t37 || $[94] !== t38 || $[95] !== t39) {
    t40 = <div className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 items-center gap-3 rounded-full bg-surface-scrim-default px-4 py-2 text-body text-white">{t31}{t32}{t37}{t38}{t39}</div>;
    $[92] = t31;
    $[93] = t37;
    $[94] = t38;
    $[95] = t39;
    $[96] = t40;
  } else {
    t40 = $[96];
  }
  let t41;
  if ($[97] !== handleTouchEnd || $[98] !== t13 || $[99] !== t14 || $[100] !== t16 || $[101] !== t19 || $[102] !== t22 || $[103] !== t23 || $[104] !== t25 || $[105] !== t40) {
    t41 = <DialogPrimitive.Portal>{t11}<DialogPrimitive.Content className={t12} onClick={t13} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} aria-label={t14}>{t16}{t19}{t22}{t23}{t25}{t40}</DialogPrimitive.Content></DialogPrimitive.Portal>;
    $[97] = handleTouchEnd;
    $[98] = t13;
    $[99] = t14;
    $[100] = t16;
    $[101] = t19;
    $[102] = t22;
    $[103] = t23;
    $[104] = t25;
    $[105] = t40;
    $[106] = t41;
  } else {
    t41 = $[106];
  }
  let t42;
  if ($[107] !== handleOpenChange || $[108] !== open || $[109] !== t41) {
    t42 = <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>{t41}</DialogPrimitive.Root>;
    $[107] = handleOpenChange;
    $[108] = open;
    $[109] = t41;
    $[110] = t42;
  } else {
    t42 = $[110];
  }
  return t42;
}
function _temp6(s_4) {
  return s_4.goToPrev;
}
function _temp5(s_3) {
  return s_3.goToNext;
}
function _temp4(s_2) {
  return s_2.closeLightbox;
}
function _temp3(s_1) {
  return s_1.currentIndex;
}
function _temp2(s_0) {
  return s_0.images;
}
function _temp(s) {
  return s.open;
}